import { Spinner } from "components/Spinner";
import { FC, HTMLAttributes, useEffect, useMemo, useRef, useState } from "react";
import styled from "styled-components";
import { StageSize, SpinDirection } from "types";
import { useCurrency, usePlayer, useSkip } from "hooks";
import { TextStyle } from "pixi.js";
import { useDebounceCallback, useEventListener, useWindowSize } from "usehooks-ts";
import { getRandomRange } from "utils/number";
import { useStore } from "utils/store";
import { Center, Header as HeaderStyle } from "utils/constants";
import { useTranslation } from "react-i18next";

export type BountiesWonProps = HTMLAttributes<HTMLDivElement> & {
  onBankUpdate: () => void;
};

export const BountiesWon: FC<BountiesWonProps> = ({
  onBankUpdate,
  ...props
}) => {
  const { t } = useTranslation();
  const skip = useSkip();
  const { parseCurrency } = useCurrency();
  const {  bank } = usePlayer();
  const bankRef = useRef(-1);
  const showBank = useStore(state => state.showBank);
  const textBoxRef = useRef<HTMLDivElement>(null);
  const { width } = useWindowSize();
  const [stageSize, setStageSize] = useState<StageSize>({
    width: 0,
    height: 0,
  });
  const textStyle = useRef(new TextStyle({
    align: "center",
    fontFamily: "Teko",
    fontWeight: "500",
    fill: "#0f0",
  }));
  
  const handleResize = () => {
    textStyle.current = new TextStyle({
      ...textStyle.current,
      fontSize: Math.max(24, Math.min(44, width * 0.07 - 1.7)),
    });
    setStageSize({
      width: (textBoxRef.current?.clientWidth || 0) - 4,
      height: (textBoxRef.current?.clientHeight || 0) - 4,
    });
  };

  const debounceResize = useDebounceCallback(handleResize, 250);
  useEventListener('resize', debounceResize);

  useEffect(() => {
    handleResize();
  }, []);

  useEffect(() => {
    if(bankRef.current === -1) bankRef.current = bank;
    if(showBank && bankRef.current === bank) onBankUpdate();
  }, [showBank, bank, onBankUpdate]);

  const bounties = useMemo(() => {
    if(!bank) return [0];
    return getRandomRange(Math.max(0, bankRef.current), bank);
  }, [bank]);

  const debBankUpdate = useDebounceCallback(() => {
    if(showBank) onBankUpdate();
  }, skip ? 0 : 500);

  const handleSpin = () => {
    bankRef.current = bank;
    debBankUpdate();
  };

  return (
    <Container>
    <HeaderContainer>
      <Image src="images/bounty-green.webp" alt="bounty green" height="100%" />
      <Header>{t('BOUNTIES WON')}</Header>
    </HeaderContainer>
      <TextBox ref={textBoxRef}>
        <Spinner
          {...stageSize}
          textStyle={textStyle.current}
          options={bounties.map((bounty) => parseCurrency(bounty))}
          spinTo={parseCurrency(bank)}
          spinSeconds={2}
          spinDirection={SpinDirection.Up}
          onSpinFinish={handleSpin}
          skip={skip}
        />
      </TextBox>
    </Container>
  );
};

const Container = styled.div`
  width: 100%;
  height: 100%;
  border-radius: 1.1vh;
  background-image: linear-gradient(
    to bottom,
    #cf9b5e 0%,
    #efe090 50%,
    #cf9b5e 100%
  );
  position: relative;
  grid-column: 2 / 3;
  grid-row: 1 / 2;

  &::before {
    content: "";
    position: absolute;
    width: calc(100% - 4px);
    height: calc(100% - 4px);
    left: 2px;
    top: 2px;
    border-radius: 1.1vh;
    background-color: #000;
  }

  @media (width < 500px){
    &::before {
      width: calc(100% - 2px);
      height: calc(100% - 2px);
      left: 1px;
      top: 1px;
    }
  }
`;

const TextBox = styled.div`
  background-image: linear-gradient(to bottom, #6e6157 3%, #61533b 21%, #cdc28e 54%, #5b4e36 78%, #6c5e51);
  border-radius: 0.9vh;
  position: relative;
  margin: 0.5vh auto 0;
  padding: 2px 0;
  width: calc(100% - 8.1vw);
  height: 4.1vh;
  z-index: 0;
  user-select: none;

  &::before {
    content: "";
    position: absolute;
    border-radius: 0.9vh;
    left: 2px;
    top: 2px;
    width: calc(100% - 4px);
    height: calc(100% - 4px);
    background-color: #000;
    z-index: -1;
  }

  @media (width < 500px){
    &::before {
      width: calc(100% - 2px);
      height: calc(100% - 2px);
      left: 1px;
      top: 1px;
    }
  }

  &::after {
    content: "";
    position: absolute;
    left: -8px;
    top: calc(100% / 2 - 4px);
    width: calc(100% + 16px);
    height: 100%;
    background-image: conic-gradient(
        rgba(0, 0, 0, 0) 45deg,
        #968963 45deg,
        #c9be8a 135deg,
        rgba(0, 0, 0, 0) 135deg
      ),
      conic-gradient(
        rgba(0, 0, 0, 0) 225deg,
        #c9be8a 225deg,
        #968963 315deg,
        rgba(0, 0, 0, 0) 315deg
      );
    background-size: 8px 8px;
    background-position: 0 0, calc(100% - 1px) 0;
    background-repeat: no-repeat;
  }
`;

const Image = styled.img`
  object-fit: contain;
`;

const HeaderContainer = styled(Center)`
  width: 31.9vw;
  height: 2.8vh;
  position: relative;
  margin: 1.5vh auto 0;

  & > ${Image} {
    margin-right: 3%;
  }
`;

const Header = styled(HeaderStyle)`
  margin-top: 1%;
  font-size: 4.5vw;
`;

